import {
  Button,
  Chip,
  type ChipTone,
  ConfirmCard,
  DataGrid,
  type DataGridColumn,
  Dialog,
  EmptyState,
  ErrorBanner,
  InfoChip,
  Input,
  RefreshBar,
  Select,
  SkeletonRows,
  safeStorage,
  toast,
} from '@patchbay/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Bookmark, FolderKanban, RotateCcw, Search, X } from 'lucide-react'
import * as React from 'react'
import { Frame, PaneHeader } from '../../../components/frame'
import { describeError } from '../../../lib/api/client'
import { formatWhen } from '../../../lib/display-time'
import { useMe } from '../../auth/session'
import {
  type CardOut,
  type PortfolioFooter,
  PROJECT_LANES,
  type ProjectHealth,
  type ProjectLaneDefinition,
  type ProjectListFilters,
  type ProjectPlatform,
  type ProjectRow,
  type ProjectState,
  projectKeys,
  projectLanesQuery,
  projectListInfiniteQuery,
  projectPmOptionsQuery,
  projectsApi,
} from '../api'
import './portfolio.css'

const PLATFORMS: readonly { value: ProjectPlatform; label: string }[] = [
  { value: 'avaya', label: 'Avaya' },
  { value: 'smartpbx', label: 'SmartPBX' },
  { value: 'mitel', label: 'Mitel' },
  { value: 'network', label: 'Network' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'btc_integration', label: 'BTC integration' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'other', label: 'Other' },
]
const PLATFORM_VALUES = new Set<string>(PLATFORMS.map((platform) => platform.value))

export interface PortfolioSearch {
  pm?: string
  platform?: ProjectPlatform
  q?: string
}

export function parsePortfolioSearch(raw: Record<string, unknown>): PortfolioSearch {
  const q = typeof raw.q === 'string' ? raw.q.trim().slice(0, 120) : ''
  return {
    pm: typeof raw.pm === 'string' && raw.pm.length <= 64 ? raw.pm : undefined,
    platform: typeof raw.platform === 'string' && PLATFORM_VALUES.has(raw.platform) ? (raw.platform as ProjectPlatform) : undefined,
    q: q || undefined,
  }
}

function words(value: string): string {
  return value
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

function healthTone(health: ProjectHealth | undefined): ChipTone {
  if (health === 'green') return 'success'
  if (health === 'amber') return 'warning'
  if (health === 'red') return 'danger'
  return 'neutral'
}

function stateTone(state: ProjectState): ChipTone {
  if (state === 'closed') return 'success'
  if (state === 'on_hold') return 'warning'
  if (state === 'cancelled') return 'neutral'
  return 'outline'
}

function HealthChip({ row }: { row: ProjectRow }) {
  const reasons = row.health_reasons ?? []
  const label = words(row.health ?? 'unknown')
  if (!reasons.length) return <Chip tone={healthTone(row.health)}>{label}</Chip>
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <InfoChip tone={healthTone(row.health)} info={reasons.map((reason) => reason.label).join(' · ')}>
        {label}
      </InfoChip>
      <span className="max-pane:hidden text-xs text-muted-foreground">{reasons.map((reason) => reason.label).join(' · ')}</span>
    </div>
  )
}

function nextMilestone(row: ProjectRow): React.ReactNode {
  if (!row.next_milestone_kind && !row.next_milestone_planned_at) return <span className="text-subtle">Not scheduled</span>
  return (
    <span className="block min-w-0">
      <span className="block truncate">{row.next_milestone_kind ? words(row.next_milestone_kind) : 'Milestone'}</span>
      <span className="block text-xs text-muted-foreground">
        {row.next_milestone_planned_at
          ? formatWhen(
              row.next_milestone_planned_at.includes('T') ? row.next_milestone_planned_at : `${row.next_milestone_planned_at}T12:00:00Z`,
              { style: 'date' },
            )
          : 'Date unknown'}
      </span>
    </span>
  )
}

const NEXT_STATE: Partial<Record<ProjectState, { label: string; to: ProjectState }>> = {
  intake: { label: 'Plan', to: 'planning' },
  planning: { label: 'Schedule', to: 'scheduled' },
  scheduled: { label: 'Start work', to: 'in_progress' },
  in_progress: { label: 'Start closeout', to: 'closeout' },
  closeout: { label: 'Close', to: 'closed' },
  closed: { label: 'Reopen', to: 'closeout' },
  cancelled: { label: 'Reopen', to: 'planning' },
}

function cardStatus(card: CardOut): 'pending' | 'confirmed' | 'dismissed' | 'failed' {
  if (card.state === 'confirmed' || card.state === 'edited_confirmed') return 'confirmed'
  if (card.state === 'dismissed' || card.state === 'expired') return 'dismissed'
  if (card.state === 'failed') return 'failed'
  return 'pending'
}

function ProjectAction({ row }: { row: ProjectRow }) {
  const queryClient = useQueryClient()
  const action = NEXT_STATE[row.state]
  const [open, setOpen] = React.useState(false)
  const [card, setCard] = React.useState<CardOut | null>(null)
  const propose = useMutation({
    mutationFn: async () => {
      if (row.state === 'closed' || row.state === 'cancelled') {
        return projectsApi.proposeReopen({ path: { project_id: row.id }, body: { project_id: row.id } })
      }
      if (row.state === 'closeout') {
        return projectsApi.proposeClose({ path: { project_id: row.id }, body: { project_id: row.id } })
      }
      if (!action) throw new Error('No project action is available')
      return projectsApi.proposeSetState({
        path: { project_id: row.id },
        body: { project_id: row.id, from_state: row.state, to_state: action.to },
      })
    },
    onSuccess: (next) => setCard(next),
  })
  const decide = useMutation({
    mutationFn: async ({ confirm, reason }: { confirm: boolean; reason?: string | null }) => {
      if (!card) throw new Error('No card is open')
      return confirm ? projectsApi.confirmCard(card.id) : projectsApi.dismissCard(card.id, reason)
    },
    onSuccess: (next) => {
      setCard(next)
      void queryClient.invalidateQueries({ queryKey: projectKeys.all })
      toast.success(next.state === 'dismissed' ? 'Project change dismissed' : 'Project change confirmed')
    },
    onError: (error) => toast.error(`Project unchanged: ${describeError(error)}`),
  })
  if (!action) {
    return (
      <Button size="sm" variant="ghost" render={<a href={`/project/${row.id}`} />}>
        Open
      </Button>
    )
  }
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setCard(null)
          setOpen(true)
          propose.mutate()
        }}
      >
        {action.label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={`${action.label} ${row.number}`}
        description="Review the server-created card before confirming this project change."
      >
        {propose.isPending ? <SkeletonRows count={2} /> : null}
        {propose.isError ? (
          <ErrorBanner title="Project card was not created" detail={describeError(propose.error)} onRetry={() => propose.mutate()} />
        ) : null}
        {card ? (
          <ConfirmCard
            kind={card.kind}
            title={card.title ?? `${action.label} ${row.number}`}
            impact={{ audience: card.audience === 'unclassified' ? 'internal' : card.audience, text: card.impact_text }}
            executor={card.executor}
            status={decide.isPending ? 'running' : cardStatus(card)}
            outcome={decide.isError ? describeError(decide.error) : undefined}
            onConfirm={card.can_confirm ? () => decide.mutate({ confirm: true }) : undefined}
            onDismiss={card.can_dismiss ? (reason) => decide.mutate({ confirm: false, reason }) : undefined}
          >
            {card.preview ? <pre className="overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify(card.preview, null, 2)}</pre> : null}
          </ConfirmCard>
        ) : null}
      </Dialog>
    </>
  )
}

function money(value: string | null | undefined, currency: string | undefined): string {
  if (value == null) return 'unknown'
  return currency ? `${currency} ${value}` : value
}

export function PortfolioSummary({ footer }: { footer: PortfolioFooter }) {
  const hasForecast = Object.hasOwn(footer, 'forecast_revenue_this_month')
  const hasTarget = Object.hasOwn(footer, 'monthly_revenue_target')
  const hasRemaining = Object.hasOwn(footer, 'remaining_to_collect')
  return (
    <footer
      role="contentinfo"
      aria-label="Portfolio summary"
      className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-border px-4 py-3 text-sm sm:px-5"
    >
      <span>{footer.open_count === 'unknown' ? 'Open projects unknown' : `${footer.open_count} open projects`}</span>
      {hasForecast ? <span>Forecast {money(footer.forecast_revenue_this_month, footer.currency)}</span> : null}
      {hasTarget ? <span>Monthly target {money(footer.monthly_revenue_target, footer.currency)}</span> : null}
      {hasRemaining ? <span>Remaining to collect {money(footer.remaining_to_collect, footer.currency)}</span> : null}
      {footer.sources?.map((source, index) => (
        <span key={`${source.source}:${source.ref ?? index}`} className="text-xs text-muted-foreground">
          {words(source.source)}: {source.state ? words(source.state) : 'state unknown'}
          {source.as_of ? ` · ${formatWhen(source.as_of, { style: 'absolute' })}` : ''}
        </span>
      ))}
    </footer>
  )
}

interface PinnedView {
  lane: ProjectLaneDefinition['lane']
  search: PortfolioSearch
}

function pinnedKey(viewerId: string | undefined): string {
  return `patchbay-projects-pinned-view:${viewerId ?? 'unknown'}`
}

function readPinned(viewerId: string | undefined): PinnedView | null {
  const raw = safeStorage.getItem(pinnedKey(viewerId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PinnedView>
    const lane = PROJECT_LANES.find((candidate) => candidate.lane === parsed.lane)?.lane
    if (!lane) return null
    return { lane, search: parsePortfolioSearch((parsed.search ?? {}) as Record<string, unknown>) }
  } catch {
    return null
  }
}

function PinView({ lane, search }: { lane: ProjectLaneDefinition; search: PortfolioSearch }) {
  const me = useMe()
  const [pinned, setPinned] = React.useState<PinnedView | null>(() => readPinned(me?.id))
  const current = JSON.stringify({ lane: lane.lane, search })
  const isCurrent = pinned ? JSON.stringify(pinned) === current : false
  return (
    <Button
      size="sm"
      variant="outline"
      aria-pressed={isCurrent}
      onClick={() => {
        if (isCurrent) {
          safeStorage.setItem(pinnedKey(me?.id), '')
          setPinned(null)
          toast.success('Portfolio view unpinned')
          return
        }
        const next = { lane: lane.lane, search }
        safeStorage.setItem(pinnedKey(me?.id), JSON.stringify(next))
        setPinned(next)
        toast.success('Portfolio view pinned')
      }}
    >
      <Bookmark className="size-4" /> {isCurrent ? 'Pinned' : 'Pin view'}
    </Button>
  )
}

export function PortfolioScreen({
  lane,
  search,
  onSearch,
}: {
  lane: ProjectLaneDefinition
  search: PortfolioSearch
  onSearch: (patch: Partial<PortfolioSearch>) => void
}) {
  const navigate = useNavigate()
  const lanes = useQuery(projectLanesQuery())
  const pmOptions = useQuery(projectPmOptionsQuery())
  const filters: ProjectListFilters = { lane: lane.lane, ...search }
  const projects = useInfiniteQuery(projectListInfiniteQuery(filters))
  const rows = React.useMemo(() => {
    const seen = new Set<string>()
    return (projects.data?.pages.flatMap((page) => page.items) ?? []).filter((row) => {
      if (seen.has(row.id)) return false
      seen.add(row.id)
      return true
    })
  }, [projects.data])
  const openProject = React.useCallback((id: string) => void navigate({ to: `/project/${id}` as '/' }), [navigate])
  const grid = React.useRef<HTMLDivElement>(null)
  const rowCount = rows.length
  React.useEffect(() => {
    if (!rowCount) return
    for (const row of grid.current?.querySelectorAll<HTMLElement>('[data-row]') ?? []) row.tabIndex = 0
  }, [rowCount])

  const columns = React.useMemo<DataGridColumn<ProjectRow>[]>(
    () => [
      {
        id: 'project',
        header: 'Project',
        width: '12rem',
        sortValue: (row) => row.number,
        cell: (row) => (
          <a href={`/project/${row.id}`} className="block min-w-0 font-medium text-primary hover:underline">
            <span className="block truncate font-mono text-xs">{row.number}</span>
            <span className="pane:hidden block truncate text-foreground">{row.customer_display ?? row.title}</span>
            <span className="pane:hidden block truncate text-xs font-normal text-muted-foreground">
              {words(row.platform ?? 'platform unknown')} · {words(row.state)}
            </span>
          </a>
        ),
      },
      {
        id: 'health',
        header: 'Health',
        sortValue: (row) => row.health ?? 'unknown',
        cell: (row) => <HealthChip row={row} />,
      },
      {
        id: 'customer',
        header: 'Customer',
        width: '13rem',
        sortValue: (row) => row.customer_display ?? '',
        cell: (row) => row.customer_display ?? <span className="text-subtle">Customer unknown</span>,
      },
      {
        id: 'platform',
        header: 'Platform',
        sortValue: (row) => row.platform ?? '',
        cell: (row) => words(row.platform ?? 'unknown'),
      },
      {
        id: 'state',
        header: 'State',
        sortValue: (row) => row.state,
        cell: (row) => (
          <Chip tone={stateTone(row.state)} title={row.on_hold_reason ? words(row.on_hold_reason) : undefined}>
            {words(row.state)}
          </Chip>
        ),
      },
      {
        id: 'milestone',
        header: 'Next milestone',
        width: '12rem',
        sortValue: (row) => row.next_milestone_planned_at ?? '',
        cell: nextMilestone,
      },
      {
        id: 'pm',
        header: 'PM',
        sortValue: (row) => row.pm?.name ?? '',
        cell: (row) => row.pm?.name ?? <span className="text-subtle">Unassigned</span>,
      },
      { id: 'action', header: 'Action', align: 'right', cell: (row) => <ProjectAction row={row} /> },
    ],
    [],
  )

  return (
    <Frame
      active={`projects-${lane.lane}`}
      railless
      main={
        <div className="project-portfolio flex h-full min-h-0 flex-col">
          <PaneHeader
            title={`Projects · ${lane.label}`}
            subtitle="Live project state, health, next move and milestone"
            actions={<PinView lane={lane} search={search} />}
          />
          <RefreshBar active={(projects.isFetching && !projects.isPending) || lanes.isFetching} />
          <div className="flex flex-wrap items-end gap-2 border-b border-border px-4 py-3 sm:px-5">
            <label htmlFor="portfolio-pm" className="grid min-w-44 gap-1 text-xs font-medium text-muted-foreground">
              PM
              <Select
                id="portfolio-pm"
                aria-label="Filter by PM"
                value={search.pm ?? 'all'}
                disabled={pmOptions.isError}
                onValueChange={(value) => onSearch({ pm: value === 'all' ? undefined : value })}
                options={[
                  { value: 'all', label: pmOptions.isError ? 'PM list unreachable' : 'All PMs' },
                  ...(pmOptions.data?.people.flatMap((person) => (person.user_id ? [{ value: person.user_id, label: person.name }] : [])) ??
                    []),
                ]}
              />
            </label>
            <label htmlFor="portfolio-platform" className="grid min-w-44 gap-1 text-xs font-medium text-muted-foreground">
              Platform
              <Select
                id="portfolio-platform"
                aria-label="Filter by platform"
                value={search.platform ?? 'all'}
                onValueChange={(value) => onSearch({ platform: value === 'all' ? undefined : (value as ProjectPlatform) })}
                options={[{ value: 'all', label: 'All platforms' }, ...PLATFORMS]}
              />
            </label>
            <label className="grid min-w-52 flex-1 gap-1 text-xs font-medium text-muted-foreground">
              Search
              <span className="relative">
                <Search aria-hidden className="absolute top-2 left-2.5 size-4 text-subtle" />
                <Input
                  aria-label="Search projects"
                  value={search.q ?? ''}
                  maxLength={120}
                  className="w-full pl-8"
                  placeholder="Project, customer or title"
                  onChange={(event) => onSearch({ q: event.currentTarget.value || undefined })}
                />
              </span>
            </label>
            {search.pm || search.platform || search.q ? (
              <Button size="sm" variant="ghost" onClick={() => onSearch({ pm: undefined, platform: undefined, q: undefined })}>
                <X className="size-4" /> Clear
              </Button>
            ) : null}
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-5">
            {projects.isPending ? <SkeletonRows count={4} /> : null}
            {projects.isError ? (
              <ErrorBanner
                title="Projects are unreachable"
                detail={`${describeError(projects.error)} Portfolio state is unknown, not empty.`}
                onRetry={() => void projects.refetch()}
              />
            ) : null}
            {projects.data ? (
              // Event delegation gives each generated DataGrid row one click/Enter destination without nesting controls.
              // biome-ignore lint/a11y/noStaticElementInteractions: child rows receive tabindex and own the interaction semantics
              <div
                ref={grid}
                onClick={(event) => {
                  if (!(event.target instanceof Element) || event.target.closest('a, button, input, [role="combobox"]')) return
                  const hit = event.target.closest<HTMLElement>('[data-row]')
                  if (hit?.dataset.row) openProject(hit.dataset.row)
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' || !(event.target instanceof HTMLElement) || !event.target.matches('[data-row]')) return
                  if (event.target.dataset.row) openProject(event.target.dataset.row)
                }}
              >
                <DataGrid
                  data={rows}
                  columns={columns}
                  rowKey={(row) => row.id}
                  label={`${lane.label} projects`}
                  empty={
                    <EmptyState
                      icon={<FolderKanban />}
                      title="No projects matched this view"
                      detail="The project service answered successfully. Change a filter or choose another lane."
                    />
                  }
                />
              </div>
            ) : null}
            {projects.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  pending={projects.isFetchingNextPage}
                  disabled={projects.isFetchingNextPage}
                  disabledReason="Loading more projects"
                  onClick={() => void projects.fetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
          {lanes.data ? <PortfolioSummary footer={lanes.data.footer} /> : null}
          {lanes.isError ? (
            <div className="border-t border-border px-4 py-2 text-sm text-warning">
              Portfolio totals are unreachable; counts and forecast are unknown.
              <Button size="sm" variant="ghost" onClick={() => void lanes.refetch()}>
                <RotateCcw className="size-4" /> Retry
              </Button>
            </div>
          ) : null}
        </div>
      }
    />
  )
}
